// Global Instructions Rule Applied!
// Shape returned by wf__waterfall_detail and by the two writes
// (wf__save_waterfall, wf__create_from_template): a fund's open distribution
// waterfall - the header, its tiers in sequence with the splits of each tier,
// the GP carry participants, and has_booked_distributions, the lock once a
// Distribution fund transaction is booked (docs/WATERFALL_CARRY_DESIGN.md,
// phase 1). Lookups are ids; their labels come from the global configs.
// Mirrors backend/src/data_manager/data_models/fund_waterfall_record.py.
// Ids are plain strings: funds, accounts and currencies carry Dataverse GUIDs
// whose version nibble fails zod's RFC-4122 .uuid() check (the fund terms
// record types them the same way).
import { z } from 'zod'

export const WaterfallSplitSchema = z
  .object({
    id: z.string(),
    recipient_class_id: z.number().int(),
    rate_source_id: z.number().int().nullish(),
    // the figure for the 'fixed' source; null for a sourced split
    split_percent: z.number().nullish()
  })
  .strict()

export const WaterfallTierSchema = z
  .object({
    id: z.string(),
    sequence: z.number().int(),
    tier_type_id: z.number().int(),
    stop_metric_id: z.number().int().nullish(),
    stop_base_id: z.number().int().nullish(),
    stop_threshold: z.number().nullish(),
    threshold_source_id: z.number().int().nullish(),
    stop_tier_group: z.string().nullish(),
    stop_recipient_class_id: z.number().int().nullish(),
    compounding_frequency_id: z.number().int().nullish(),
    day_count_convention_id: z.number().int().nullish(),
    reference: z.string().nullish(),
    notes: z.string().nullish(),
    // the pools a Cross-Pool Loss Offset tier offsets against; none = every
    // other pool (phase 6 PR 3)
    pool_targets: z.array(z.string()),
    splits: z.array(WaterfallSplitSchema)
  })
  .strict()

// an investment of a pool: an ultimate-parent tranche with its whole lineage
export const WaterfallPoolTrancheSchema = z
  .object({
    tranche_id: z.string(),
    tranche_name: z.string().nullish(),
    company_id: z.string().nullish(),
    company_name: z.string().nullish(),
    bought: z.string().nullish(),
    cost: z.number(),
    cost_divested: z.number(),
    proceeds: z.number()
  })
  .strict()

// a pool of the waterfall with its figures as at pools_as_of (wf__pool_results,
// the one source of every pool figure) and its investments
export const WaterfallPoolSchema = z
  .object({
    id: z.string(),
    pool_code: z.string(),
    pool_type: z.string(),
    start_date: z.string().nullish(),
    end_date: z.string().nullish(),
    investments: z.number().int(),
    cost: z.number(),
    cost_divested: z.number(),
    proceeds: z.number(),
    net_realized: z.number(),
    contributions: z.number(),
    capital_returned: z.number(),
    remediation_received: z.number(),
    remediation_given: z.number(),
    profit_received: z.number(),
    carry_paid: z.number(),
    capital_due: z.number(),
    unremedied_loss: z.number(),
    tranches: z.array(WaterfallPoolTrancheSchema)
  })
  .strict()

// what falls in no pool: contribution | realization | journal | investment
// (a write-off is a realization: cost divested for no proceeds)
export const WaterfallPoolGapSchema = z
  .object({
    kind: z.string(),
    ref_id: z.string().nullish(),
    label: z.string().nullish(),
    gap_date: z.string().nullish(),
    amount: z.number().nullish()
  })
  .strict()

export const WaterfallRecipientSchema = z
  .object({
    id: z.string(),
    account_id: z.string(),
    account_name: z.string().nullish(),
    allocation_percent: z.number(),
    effective_from: z.string(),
    effective_to: z.string().nullish(),
    notes: z.string().nullish()
  })
  .strict()

export const FundWaterfallHeaderSchema = z
  .object({
    id: z.string(),
    fund_id: z.string(),
    name: z.string(),
    basis_id: z.number().int().nullish(),
    scope_id: z.number().int(),
    // cfg.waterfall_history_checks: how a new distribution checks the earlier
    // ones; null = the engine's default, to date (phase 6)
    history_check_id: z.number().int().nullish(),
    currency_id: z.string().nullish(),
    effective_from: z.string().nullish(),
    effective_to: z.string().nullish(),
    recallable_pct: z.number().nullish(),
    template_id: z.string().nullish(),
    source_reference: z.string().nullish(),
    notes: z.string().nullish(),
    date_modified: z.string().nullish()
  })
  .strict()

export const FundWaterfallRecordSchema = z
  .object({
    fund_id: z.string(),
    // null while the fund has no open waterfall
    waterfall: FundWaterfallHeaderSchema.nullable(),
    tiers: z.array(WaterfallTierSchema),
    recipients: z.array(WaterfallRecipientSchema),
    has_booked_distributions: z.boolean(),
    pools: z.array(WaterfallPoolSchema),
    pool_gaps: z.array(WaterfallPoolGapSchema),
    pools_as_of: z.string(),
    // only on the answer of Create year pools: what it added
    year_pools: z
      .object({
        pools_created: z.number().int(),
        tranches_assigned: z.number().int(),
        tranches_unplaced: z.number().int()
      })
      .strict()
      .nullish()
  })
  .strict()

export type WaterfallSplit = z.infer<typeof WaterfallSplitSchema>
export type WaterfallTier = z.infer<typeof WaterfallTierSchema>
export type WaterfallPool = z.infer<typeof WaterfallPoolSchema>
export type WaterfallPoolTranche = z.infer<typeof WaterfallPoolTrancheSchema>
export type WaterfallPoolGap = z.infer<typeof WaterfallPoolGapSchema>
export type WaterfallRecipient = z.infer<typeof WaterfallRecipientSchema>
export type FundWaterfallHeader = z.infer<typeof FundWaterfallHeaderSchema>
export type FundWaterfallRecord = z.infer<typeof FundWaterfallRecordSchema>

// What the section sends to POST /funds/:id/waterfall (the fund id comes
// from the route). Tiers are ordered: sequence = position.
export interface WaterfallSplitPayload {
  id?: string
  recipient_class_id: number
  rate_source_id: number
  split_percent?: number | null
}

export interface WaterfallTierPayload {
  id?: string
  tier_type_id: number
  stop_metric_id: number
  stop_base_id?: number | null
  threshold_source_id?: number | null
  stop_threshold?: number | null
  stop_tier_group?: string | null
  stop_recipient_class_id?: number | null
  compounding_frequency_id?: number | null
  day_count_convention_id?: number | null
  reference?: string | null
  notes?: string | null
  /** a Cross-Pool Loss Offset tier only: a replacement set; absent or empty = every other pool */
  pool_targets?: string[]
  splits: WaterfallSplitPayload[]
}

export interface FundWaterfallSavePayload {
  id?: string
  name: string
  basis_id: number
  scope_id: number
  // cfg.waterfall_history_checks; null = the engine's default (to date)
  history_check_id?: number | null
  currency_id?: string | null
  effective_from?: string | null
  recallable_pct?: number | null
  source_reference?: string | null
  notes?: string | null
  tiers: WaterfallTierPayload[]
}

export const validateFundWaterfallRecord = (data: unknown): FundWaterfallRecord => {
  const result = FundWaterfallRecordSchema.safeParse(data)
  if (!result.success) {
    throw new Error(`Fund waterfall validation failed: ${result.error.message}`)
  }
  return result.data
}
